import React, { useState } from 'react'
import dayjs from 'dayjs'
import { format, parseISO } from 'date-fns'
import { CalendarIcon, Loader2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { useUpdateProjectScheduleDatesMutation } from '@/redux/service/project'
import { cn, formatCurrency } from '@/lib/utils'

interface InfoRowProps {
  label: string
  value: React.ReactNode
}

const InfoRow = ({ label, value }: InfoRowProps) => (
  <div className='flex items-center justify-between gap-4 py-2.5'>
    <span className='text-sm text-muted-foreground'>{label}</span>
    <span className='text-sm font-bold text-foreground text-right'>
      {value ?? '—'}
    </span>
  </div>
)

const Pill = ({
  children,
  tone = 'solid-blue',
}: {
  children: React.ReactNode
  tone?:
    | 'solid-blue'
    | 'outline-blue'
    | 'solid-amber'
    | 'solid-red'
    | 'solid-emerald'
}) => {
  const toneClasses: Record<string, string> = {
    'solid-blue': 'bg-blue-600 text-white border border-blue-600',
    'outline-blue': 'bg-transparent text-blue-600 border border-blue-600',
    'solid-amber': 'bg-amber-500 text-white border border-amber-500',
    'solid-red': 'bg-red-600 text-white border border-red-600',
    'solid-emerald': 'bg-emerald-600 text-white border border-emerald-600',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold capitalize leading-none',
        toneClasses[tone]
      )}
    >
      {children}
    </span>
  )
}

const InfoCard = ({
  title,
  children,
  className,
}: {
  title: string
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={cn(
      'rounded-2xl bg-card border border-border/60 shadow-sm p-6',
      className
    )}
  >
    <h3 className='text-base font-bold text-foreground pb-3 mb-1 border-b border-border/60'>
      {title}
    </h3>
    <div className='divide-y divide-border/40'>{children}</div>
  </div>
)

const EditableDateRow = ({
  label,
  value,
  listId,
  field,
  otherFieldValue,
  isLoading,
  onSave,
}: {
  label: string
  value?: string | null | undefined
  listId: string | number
  field: 'start_date' | 'due_date'
  otherFieldValue?: string | null | undefined
  isLoading: boolean
  onSave: (
    listId: string | number,
    field: 'start_date' | 'due_date',
    isoDate: string | null
  ) => Promise<void>
}) => {
  const [open, setOpen] = useState(false)

  const save = async (isoDate: string | null) => {
    if (field === 'start_date' && isoDate && otherFieldValue) {
      if (parseISO(otherFieldValue) < parseISO(isoDate)) {
        toast.error('Due Date cannot be earlier than Start Date')
        return
      }
    }
    if (field === 'due_date' && isoDate && otherFieldValue) {
      if (parseISO(isoDate) < parseISO(otherFieldValue)) {
        toast.error('Due Date cannot be earlier than Start Date')
        return
      }
    }

    try {
      await onSave(listId, field, isoDate)
      setOpen(false)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(
        error?.data?.message ??
          'Could not update ClickUp — the date was not changed'
      )
    }
  }

  return (
    <div className='flex items-center justify-between gap-4 py-2.5'>
      <span className='text-sm text-muted-foreground'>{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            disabled={isLoading}
            className='inline-flex items-center gap-1.5 text-sm font-bold text-foreground hover:text-primary transition-colors disabled:opacity-50'
          >
            {isLoading ? (
              <Loader2 className='w-3.5 h-3.5 animate-spin' />
            ) : (
              <CalendarIcon className='w-3.5 h-3.5 opacity-50' />
            )}
            {value ? format(parseISO(value), 'dd MMM, yyyy') : '—'}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align='end'
          className='w-auto p-0 border border-border shadow-lg rounded-xl'
        >
          <Calendar
            mode='single'
            captionLayout='dropdown'
            fromYear={2018}
            toYear={2035}
            selected={value ? parseISO(value) : undefined}
            onSelect={date => date && save(format(date, 'yyyy-MM-dd'))}
          />
          <div className='flex justify-end p-2 border-t border-border'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 text-xs text-muted-foreground'
              onClick={() => save(null)}
              disabled={!value || isLoading}
            >
              <X className='w-3 h-3 mr-1' />
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface ProjectOverviewCardsProps {
  status?: string | null
  priority?: string | null
  billingType?: string | null
  communication?: string | null
  projectType?: string | null
  techStack?: string[] | null
  teamName?: string | null
  department?: string | null
  deliveryHead?: string | null
  teamLeader?: string | null
  accountManager?: string | null
  qaStatus?: string | null
  milestoneTotal?: number | null | undefined
  timelineHours?: React.ReactNode
  startDate?: string | null
  dueDate?: string | null
  dateDone?: string | null
  // Vendor summary card - only rendered when the project has vendor work.
  hasVendorWork?: boolean
  vendorName?: string | null | undefined
  vendorPaymentType?: 'full' | 'milestone' | null | undefined
  // Hourly rate - only meaningful/shown for hourly-type vendors, finance-only
  vendorRate?: number | null
  // Cost of vendor work (hourly hours × rate, or the committed full/
  // milestone amount) - already merged server-side regardless of type,
  // so this is always "what we're giving the vendor" either way.
  vendorCost?: number | null
  // Hours only mean something for hourly-paid vendors (payment type null) -
  // full/milestone vendors are paid the same amount whether hours were
  // logged or not, so hours are hidden entirely for them (see
  // ProjectView.tsx's SummaryTable for the matching fix).
  vendorHours?: number | null
  billedRevenue?: number | null
  // Finance-only (accessF server-side) - gates Given to Vendor.
  canViewVendorFinance?: boolean
  // Finance OR mini_finance (accessF || accessMF server-side) - gates
  // We're Getting (project_billable_amount), which mini_finance is
  // allowed to see unlike vendor_cost above.
  canViewRevenue?: boolean
  listId?: string | number
  canEditDates?: boolean
}

const dash = (value?: string | number | null) =>
  value === null || value === undefined || value === '' ? '—' : value

const ProjectOverviewCards: React.FC<ProjectOverviewCardsProps> = ({
  status,
  priority,
  billingType,
  communication,
  projectType,
  techStack,
  teamName,
  department,
  deliveryHead,
  teamLeader,
  accountManager,
  qaStatus,
  milestoneTotal,
  timelineHours,
  startDate,
  dueDate,
  dateDone,
  hasVendorWork,
  vendorName,
  vendorPaymentType,
  vendorRate,
  vendorCost,
  vendorHours,
  billedRevenue,
  canViewVendorFinance,
  canViewRevenue,
  listId,
  canEditDates,
}) => {
  // has_vendor_work and the actual vendor link are set independently
  // server-side (updateProjectMeta only blocks completing setup in that
  // state, not setting has_vendor_work alone) - a project mid-setup can
  // have has_vendor_work=true with no vendor chosen yet, in which case
  // vendorName/vendorPaymentType are both null. Don't read that as
  // "hourly vendor" - there's no vendor at all yet.
  const hasLinkedVendor = hasVendorWork && !!vendorName
  const isHourlyVendor = hasLinkedVendor && !vendorPaymentType

  const [updateDates, { isLoading: isSavingDates }] =
    useUpdateProjectScheduleDatesMutation()

  const handleSaveDate = async (
    saveListId: string | number,
    field: 'start_date' | 'due_date',
    isoDate: string | null
  ) => {
    await updateDates({ list_id: saveListId, [field]: isoDate }).unwrap()
  }

  const due = dueDate ? dayjs(dueDate) : null
  const today = dayjs()

  const dayRemaining = (() => {
    if (dateDone) return { label: 'Completed', tone: 'solid-emerald' as const }
    if (!due) return null
    const diff = due.diff(today, 'day')
    if (diff < 0)
      return {
        label: `${Math.abs(diff)} Days Overdue`,
        tone: 'solid-red' as const,
      }
    if (diff === 0) return { label: 'Due Today', tone: 'solid-red' as const }
    return { label: `${diff} Days`, tone: 'solid-amber' as const }
  })()

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-6 mb-6',
        hasVendorWork ? 'md:grid-cols-4' : 'md:grid-cols-3'
      )}
    >
      <InfoCard title='Project Details'>
        <InfoRow
          label='Status'
          value={<Pill tone='solid-blue'>{dash(status)}</Pill>}
        />
        <InfoRow
          label='Priority'
          value={<Pill tone='outline-blue'>{dash(priority)}</Pill>}
        />
        <InfoRow label='Billing Type' value={dash(billingType)} />
        <InfoRow label='Communication' value={dash(communication)} />
        <InfoRow label='Project Type' value={dash(projectType)} />
        <InfoRow
          label='Tech Stack'
          value={
            techStack && techStack.length > 0 ? (
              <div className='flex flex-wrap justify-end gap-1.5'>
                {techStack.map(tech => (
                  <Pill key={tech} tone='outline-blue'>
                    {tech}
                  </Pill>
                ))}
              </div>
            ) : (
              '—'
            )
          }
        />
      </InfoCard>

      <InfoCard title='Team Details'>
        <InfoRow label='Team Name' value={dash(teamName)} />
        <InfoRow label='Department' value={dash(department)} />
        <InfoRow label='Delivery Head' value={dash(deliveryHead)} />
        <InfoRow label='Team Leader' value={dash(teamLeader)} />
        <InfoRow label='Account Manager' value={dash(accountManager)} />
        <InfoRow label='QA' value={dash(qaStatus)} />
      </InfoCard>

      <InfoCard title='Timeline Details'>
        <InfoRow
          label='Milestone'
          value={
            milestoneTotal ? (
              <Pill tone='solid-blue'>
                {String(milestoneTotal).padStart(2, '0')}
              </Pill>
            ) : (
              '—'
            )
          }
        />
        <InfoRow label='Timeline' value={timelineHours ?? '—'} />
        {canEditDates && listId !== undefined ? (
          <>
            <EditableDateRow
              label='Start Date'
              value={startDate}
              listId={listId}
              field='start_date'
              otherFieldValue={dueDate}
              isLoading={isSavingDates}
              onSave={handleSaveDate}
            />
            <EditableDateRow
              label='Due Date'
              value={dueDate}
              listId={listId}
              field='due_date'
              otherFieldValue={startDate}
              isLoading={isSavingDates}
              onSave={handleSaveDate}
            />
          </>
        ) : (
          <>
            <InfoRow
              label='Start Date'
              value={startDate ? dayjs(startDate).format('DD MMM, YYYY') : '—'}
            />
            <InfoRow
              label='Due Date'
              value={dueDate ? dayjs(dueDate).format('DD MMM, YYYY') : '—'}
            />
          </>
        )}
        <InfoRow
          label='Day Remaining'
          value={
            dayRemaining ? (
              <Pill tone={dayRemaining.tone}>{dayRemaining.label}</Pill>
            ) : (
              '—'
            )
          }
        />
      </InfoCard>

      {hasVendorWork && (
        <InfoCard title='Vendor Details'>
          <InfoRow label='Vendor Name' value={dash(vendorName)} />
          {hasLinkedVendor && (
            <InfoRow
              label='Payment Type'
              value={
                <Pill tone='outline-blue'>{vendorPaymentType ?? 'Hourly'}</Pill>
              }
            />
          )}
          {isHourlyVendor && (
            <InfoRow
              label='Rate'
              // vendor_default_rate is already null-masked server-side for
              // roles without finance/mini_finance access, so no extra
              // frontend gate needed - matches how Rate is authorized
              // (accessF || accessMF), not the finance-only gate used for
              // Given to Vendor below.
              value={
                vendorRate != null ? `${formatCurrency(vendorRate)}/hr` : '—'
              }
            />
          )}
          <InfoRow
            label='Given to Vendor'
            // vendor_cost is finance-only server-side (accessF), unlike
            // rate/revenue above which mini_finance can also see.
            value={canViewVendorFinance ? formatCurrency(vendorCost ?? 0) : '—'}
          />
          {isHourlyVendor && (
            <InfoRow
              label='Vendor Hours'
              value={vendorHours != null ? `${vendorHours} hrs` : '—'}
            />
          )}
          <InfoRow
            label="We're Getting"
            value={canViewRevenue ? formatCurrency(billedRevenue ?? 0) : '—'}
          />
        </InfoCard>
      )}
    </div>
  )
}

export default ProjectOverviewCards
